import { useMemo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as NavbarHideIcon } from '@elementary/common-ui/assets/24x24/navbar-hide.svg';
import { ReactComponent as NavbarShowIcon } from '@elementary/common-ui/assets/24x24/navbar-show.svg';
import { ReactComponent as Logo } from '@elementary/common-ui/assets/logo.svg';
import { ReactComponent as HeartIcon } from '@elementary/common-ui/assets/heart.svg';
import {
  Flex,
  IconButton,
  FlexColumn,
  FlexCenter,
} from '@elementary/common-ui/components';
import { HEADER_HEIGHT } from '@elementary/common-ui/constants';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import { useAuth } from '@frontegg/react';
import { Divider, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import useEnvironments from 'shared/hooks/useEnvironments';
import { EnvironmentType } from '@elementary/common-ui/types';
import AccountMenu from './AccountMenu';
import AgentAvatarButton from './AgentAvatarButton';
import EnvMenu from './EnvMenu/EnvMenu';

const Wrapper = styled(Flex)<{ $environmentType?: EnvironmentType | null }>`
  height: ${HEADER_HEIGHT}px;
  min-height: ${HEADER_HEIGHT}px;
  background: ${({ theme, $environmentType }) =>
    $environmentType === 'prod'
      ? theme.palette.common.lightBlue6
      : theme.palette.common.gray15};

  border-bottom: ${({ theme }) => `1px solid ${theme.palette.common.gray16}`};
  align-items: center;
  justify-content: space-between;
`;

const LogoWrapper = styled(FlexCenter)`
  width: ${HEADER_HEIGHT}px;
  height: ${HEADER_HEIGHT}px;
  background: ${({ theme }) => theme.palette.common.magenta};
  cursor: pointer;
`;

const NavbarButton = styled(IconButton)`
  width: ${HEADER_HEIGHT}px;
  min-height: ${HEADER_HEIGHT}px;
  border-radius: 0;
`;

const HeartDivider = () => (
  <FlexColumn
    sx={{
      height: '100%',
      alignItems: 'center',
      gap: '3px',
      padding: '8px 0',
    }}
  >
    <Divider orientation="vertical" sx={{ flex: 1 }} />
    <HeartIcon />
    <Divider orientation="vertical" sx={{ flex: 1 }} />
  </FlexColumn>
);

type Props = {
  onNavbarButtonClick: () => void;
  isNavbarExpanded: boolean;
  hideNavbarButton?: boolean;
  hasAiPanelAccess?: boolean;
};

const Header = ({
  onNavbarButtonClick,
  isNavbarExpanded,
  hideNavbarButton,
  hasAiPanelAccess,
}: Props) => {
  const { user } = useAuth();
  const { data: account } = useAccount();
  const navigate = useNavigate();
  const { selectedEnvId, data: envData } = useEnvironments();
  const [currentDate, setCurrentDate] = useState(dayjs.tz());

  const hasEnvironments = !!envData?.[0];
  const selectedEnvData = useMemo(
    () => envData?.find((env) => env.id === selectedEnvId),
    [envData, selectedEnvId],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(dayjs.tz());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Wrapper $environmentType={selectedEnvData?.environmentType}>
      <Flex sx={{ height: '100%' }}>
        <Flex sx={{ height: '100%' }}>
          <LogoWrapper onClick={() => navigate('/')}>
            <Logo />
          </LogoWrapper>
          {!hideNavbarButton && (
            <NavbarButton onClick={onNavbarButtonClick}>
              {isNavbarExpanded ? <NavbarHideIcon /> : <NavbarShowIcon />}
            </NavbarButton>
          )}
        </Flex>
        <Divider orientation="vertical" />
      </Flex>
      <Flex sx={{ height: '100%' }}>
        <Divider orientation="vertical" />
        <Flex sx={{ height: '100%', alignItems: 'center' }}>
          <FlexColumn sx={{ gap: '2px', padding: '0 16px' }}>
            <Typography>{currentDate.format('L LT')}</Typography>
            <Typography sx={{ fontSize: '12px' }}>
              {currentDate.format('zzz (z)')}
            </Typography>
          </FlexColumn>
          {hasEnvironments ? <EnvMenu /> : <Divider orientation="vertical" />}

          <AccountMenu
            userId={user?.id}
            username={user?.name}
            email={user?.email}
            account={account?.name}
            deactivated={account?.activation_status === 'inactive'}
          />

          {hasAiPanelAccess && (
            <>
              <HeartDivider />
              <AgentAvatarButton />
            </>
          )}
        </Flex>
      </Flex>
    </Wrapper>
  );
};

export default Header;
